import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { formatClockTime } from '@/data/DateTimeUtil';

const TIMER_STORAGE_KEY = 'cooperaid.timer';

interface TimerState {
  startedAt: string | null;
  userId: number | null;
  organizationId: number | null;
}

interface TimerContextValue {
  startedAt: string | null;
  isRunning: boolean;
  elapsedSeconds: number;
  startTimer: () => void;
  stopTimer: () => TimerStopData | null;
}

export interface TimerStopData {
  startedAt: string;
  startedAtTime: string;
  stoppedAtTime: string;
  elapsedMinutes: number;
  userId: number;
  organizationId: number;
}

const TimerContext = createContext<TimerContextValue | null>(null);
const EMPTY_TIMER_STATE: TimerState = { startedAt: null, userId: null, organizationId: null };

const getTimerStorageKey = (userId?: number | null): string | null =>
  userId === undefined || userId === null ? null : `${TIMER_STORAGE_KEY}.${userId}`;

const isTimerStateForUser = (
  state: TimerState,
  userId?: number | null,
  organizationId?: number | null
): state is TimerState & { startedAt: string; userId: number; organizationId: number } =>
  !!state.startedAt &&
  state.userId === userId &&
  state.organizationId === organizationId;

const loadTimerState = (userId?: number | null, organizationId?: number | null): TimerState => {
  const storageKey = getTimerStorageKey(userId);
  if (!storageKey) {
    return EMPTY_TIMER_STATE;
  }

  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored) as TimerState;
      if (isTimerStateForUser(parsed, userId, organizationId)) {
        return parsed;
      }
      localStorage.removeItem(storageKey);
    }
  } catch {
  }
  return EMPTY_TIMER_STATE;
};

const saveTimerState = (state: TimerState) => {
  const storageKey = getTimerStorageKey(state.userId);
  if (!storageKey) {
    return;
  }

  if (state.startedAt) {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } else {
    localStorage.removeItem(storageKey);
  }
};

interface TimerProviderProps {
  userId?: number | null;
  organizationId?: number | null;
  children: ReactNode;
}

export const TimerProvider = ({ userId, organizationId, children }: TimerProviderProps) => {
  const [timerState, setTimerState] = useState<TimerState>(() => loadTimerState(userId, organizationId));
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt = isTimerStateForUser(timerState, userId, organizationId) ? timerState.startedAt : null;

  useEffect(() => {
    localStorage.removeItem(TIMER_STORAGE_KEY);
    setTimerState(loadTimerState(userId, organizationId));
  }, [organizationId, userId]);

  const updateElapsed = useCallback(() => {
    if (startedAt) {
      const now = Date.now();
      const started = new Date(startedAt).getTime();
      setElapsedSeconds(Math.floor((now - started) / 1000));
    }
  }, [startedAt]);

  useEffect(() => {
    if (startedAt) {
      updateElapsed();
      intervalRef.current = setInterval(updateElapsed, 1000);
    } else {
      setElapsedSeconds(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [startedAt, updateElapsed]);

  const startTimer = useCallback(() => {
    if (userId === undefined || userId === null || organizationId === undefined || organizationId === null) {
      return;
    }

    const now = new Date();
    const newState: TimerState = { startedAt: now.toISOString(), userId, organizationId };
    saveTimerState(newState);
    setTimerState(newState);
  }, [organizationId, userId]);

  const stopTimer = useCallback(() => {
    if (userId === undefined || userId === null || organizationId === undefined || organizationId === null) {
      return null;
    }

    const current = startedAt;
    if (!current) {
      return null;
    }

    const startedDate = new Date(current);
    const stoppedDate = new Date();

    const elapsedMs = stoppedDate.getTime() - startedDate.getTime();
    const elapsedMinutes = Math.round(elapsedMs / 60000);

    const result = {
      startedAt: current,
      startedAtTime: formatClockTime(startedDate),
      stoppedAtTime: formatClockTime(stoppedDate),
      elapsedMinutes,
      userId,
      organizationId,
    };

    const newState: TimerState = { startedAt: null, userId, organizationId };
    saveTimerState(newState);
    setTimerState(EMPTY_TIMER_STATE);

    return result;
  }, [organizationId, startedAt, userId]);

  return (
    <TimerContext.Provider
      value={{
        startedAt,
        isRunning: startedAt !== null,
        elapsedSeconds,
        startTimer,
        stopTimer,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};

export const useTimerContext = (): TimerContextValue => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimerContext must be used within a TimerProvider');
  }
  return context;
};
